import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppConfig } from '../contexts/AppConfigContext';

const MAX_AGE_MS = 24 * 60 * 60 * 1000; // TTL an toàn 24h (phòng khi quên "Công bố thay đổi")

interface Cached<T> {
  data: T;
  version: string;
  timestamp: number;
}

interface UseVersionedCacheReturn<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

/**
 * Cache dữ liệu trong localStorage, gate theo config.dataVersion + TTL 24h.
 *   - cache khớp version VÀ chưa quá 24h → dùng cache (không gọi API)
 *   - lệch version / quá 24h / chưa có → gọi fetcher(), lưu kèm version + timestamp
 * KHÔNG fallback: fetcher lỗi → error=true (trang tự xử lý → bảo trì).
 * `fetcher` nên được memo hoá (useCallback) ở phía gọi.
 */
export function useVersionedCache<T>(
  storageKey: string,
  fetcher: () => Promise<T>
): UseVersionedCacheReturn<T> {
  const { config, loading: configLoading, error: configError } = useAppConfig();
  const version = config?.dataVersion;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const settledVersion = useRef<string | null>(null);

  const load = useCallback(
    async (ver: string | undefined, force: boolean) => {
      // Thử cache
      if (!force && ver) {
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const cached = JSON.parse(raw) as Cached<T>;
            if (cached.version === ver && Date.now() - cached.timestamp < MAX_AGE_MS) {
              setData(cached.data);
              setError(false);
              setLoading(false);
              return;
            }
          }
        } catch {
          // cache hỏng → fetch lại
        }
      }

      setLoading(true);
      try {
        const fresh = await fetcher();
        setData(fresh);
        setError(false);
        if (ver) {
          try {
            localStorage.setItem(
              storageKey,
              JSON.stringify({ data: fresh, version: ver, timestamp: Date.now() })
            );
          } catch {
            // bỏ qua lỗi quota
          }
        }
      } catch (err) {
        console.error(`[useVersionedCache] ${storageKey} lỗi:`, err);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [storageKey, fetcher]
  );

  useEffect(() => {
    if (configLoading) return; // chờ biết dataVersion
    if (configError) return; // BE sập → gate lo, không fetch data
    const ver = version ?? '';
    if (settledVersion.current === ver) return;
    settledVersion.current = ver;
    void load(version, false);
  }, [configLoading, configError, version, load]);

  const refresh = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    settledVersion.current = null;
    void load(version, true);
  }, [storageKey, version, load]);

  return { data, loading, error, refresh };
}
